import {v4 as uuid} from 'uuid';

import {
  type Client,
  type ClientResponse,
  Conversation,
  type ConversationContext,
  type ConversationParticipant,
  type RequestAction,
  type StartConversationResponse,
} from '@stripe-internal/conversations-client';
import {
  type ConversationsApiClient,
  getConversationOverview,
  withConversationOverview,
} from '@stripe-internal/conversations-api-client';
import {createStore} from '@stripe-internal/state-manager';
import type {InlineDataEventBus} from '@stripe-internal/inline-data';

import type {Api} from '../../core/lib/api/api';
import type {AppConfig, AppProfile} from '../../core/types';
import {buildConversationsBackendApi} from './buildConversationsBackendApi';
import {clearStoredConversation} from './storage';
import type {ControllerResult} from './types';
import type {
  ConversationLoadingOperation,
  ConversationsBackend,
  ConversationsState,
} from '../app/types';
import {shouldContextRequestPageContext} from '../../core/lib/util/conversationUtils';
import {
  ThreadsConversationClient,
  type ThreadsConversationClientOptions,
} from './ThreadsConversationClient';

type BackendClientOptions = ThreadsConversationClientOptions & {
  transports: {uhClient: Pick<ConversationsApiClient, 'getConversation'>};
};

export class V2ConversationsBackend implements ConversationsBackend {
  readonly stateStore = createStore<ConversationsState>({
    loadingState: {list: false, get: false, send: false},
  });

  private api: ReturnType<typeof buildConversationsBackendApi>;

  constructor({
    getConfig,
    getProfile,
    getApi,
    transports,
    requestAction,
    withHistory,
  }: {
    getConfig: () => AppConfig;
    getProfile: () => AppProfile;
    getApi: () => Api;
    transports: {
      uhClient: BackendClientOptions['transports']['uhClient'];
      chatClient: Client;
      inlineDataEventBus: InlineDataEventBus;
    };
    requestAction: (action: RequestAction) => Promise<ClientResponse>;
    withHistory: boolean;
  }) {
    // enableConversationsThreads preserves the SDK's /end versus delete choice
    // for explicit leaves, and additionally ends a left chat on the backend.
    const clientOptions: BackendClientOptions = {
      transports: {
        uhClient: transports.uhClient,
        chatClient: transports.chatClient,
      },
      inlineDataEventBus: transports.inlineDataEventBus,
      api: getApi(),
      getFeatureFlags: () => getProfile().featureFlags,
      requestAction,
      withHistory,
      endChatConversationOnLeave:
        !!getProfile().featureFlags.enableConversationsThreads,
    };
    this.api = buildConversationsBackendApi({
      getConfig,
      getProfile,
      getApi,
      createConversation: (params) =>
        createThreadsConversation(params, clientOptions),
      loadConversation: (params) =>
        loadThreadsConversation(params, clientOptions),
    });
  }

  get state(): ConversationsState {
    return this.stateStore.getState();
  }

  create: ConversationsBackend['create'] = (params) =>
    this.run('get', () => this.api.create(params));

  createAndLoad: ConversationsBackend['createAndLoad'] = (params) =>
    this.run('get', async () => {
      const created = await this.api.create(params);
      if (created.type === 'error') {
        return created;
      }
      const conversation = created.data;
      try {
        const listed = await this.api.list({
          conversationIds: [conversation.id],
        });
        if (listed.type === 'error') {
          await conversation.disconnect();
          conversation.client.dispose?.();
          return listed;
        }
        const overview = listed.data.find(
          (item) => item.id === conversation.id,
        );
        if (!overview) {
          throw new Error('Conversation missing from post-create list');
        }
        if (
          getConversationOverview(conversation.context)?.id !== conversation.id
        ) {
          conversation.setContextValue('conversationOverview', overview);
        }
        return created;
      } catch (error) {
        await conversation.disconnect();
        conversation.client.dispose?.();
        throw error;
      }
    });

  load: ConversationsBackend['load'] = (id) =>
    this.run('get', () => this.api.load(id));

  list: ConversationsBackend['list'] = (filters) =>
    this.run('list', () => this.api.list(filters));

  end: ConversationsBackend['end'] = (id) =>
    this.run('get', async () => {
      const result = await this.api.end(id);
      if (result.type === 'success') {
        clearStoredConversation(id);
      }
      return result;
    });

  private pending = {list: 0, get: 0, send: 0};

  private async run<T>(
    operation: ConversationLoadingOperation,
    task: () => ControllerResult<T>,
  ): ControllerResult<T> {
    this.pending[operation] += 1;
    this.stateStore.setValue((state) => {
      state.loadingState[operation] = true;
    });
    try {
      return await task();
    } catch (error) {
      return {
        type: 'error',
        error:
          error instanceof Error ? error.message : 'Unknown controller error',
      };
    } finally {
      this.pending[operation] -= 1;
      this.stateStore.setValue((state) => {
        state.loadingState[operation] = this.pending[operation] > 0;
      });
    }
  }
}

async function createThreadsConversation(
  params: {
    participant: ConversationParticipant;
    context: ConversationContext;
    data?: Record<string, unknown>;
  },
  options: ThreadsConversationClientOptions,
): Promise<StartConversationResponse> {
  let client: ThreadsConversationClient | undefined;
  try {
    let {data} = params;
    if (shouldContextRequestPageContext(params.context)) {
      const response = await options.requestAction({
        name: 'request-action',
        action: {name: 'get-page-context'},
        waitForResponse: true,
      });
      if (response.data) {
        data = {...data, ...(response.data as Record<string, unknown>)};
      }
    }
    const response = await options.transports.uhClient.startConversation({
      participant: params.participant,
      context: params.context,
      data,
      skipInProgress: true,
      connect: false,
      syncStorage: false,
    });
    if ('error' in response) {
      return response;
    }
    const source = response.data.conversation;
    await source.disconnect();
    const conversationId = source.id;
    const overview =
      getConversationOverview(source.context) ??
      getConversationOverview(params.context);
    const context: ConversationContext = {
      ...params.context,
      ...source.context,
      bot_conversationId: conversationId,
    };
    client = new ThreadsConversationClient(
      {
        conversationId,
        chatId:
          typeof context.chat_conversationId === 'string'
            ? context.chat_conversationId
            : undefined,
      },
      options,
    );
    const model = new Conversation(client, {
      id: conversationId,
      user: params.participant,
      status: source.status,
      messages: {...source.messages},
      participants: {
        ...source.participants,
        [params.participant.id]: params.participant,
      },
      context,
      contextAfterRestore: withConversationOverview(
        conversationId,
        {},
        overview,
      ),
      cleanupOnFailedLeave: false,
      syncStorage: client.syncStorageMode,
      getSyncStorageMode: () => client!.syncStorageMode,
    });
    client.attachModel(model);
    return {code: 200, data: {conversation: model}};
  } catch (error) {
    await client?.disconnect();
    client?.dispose();
    return {
      code: 500,
      error:
        error instanceof Error
          ? error.message
          : 'Unable to create conversation',
    };
  }
}

async function loadThreadsConversation(
  params: {
    conversationId: string;
    participant: ConversationParticipant;
    context: ConversationContext;
  },
  options: BackendClientOptions,
): Promise<StartConversationResponse> {
  const response = await options.transports.uhClient.getConversation({
    conversationId: params.conversationId,
    participant: params.participant,
    context: params.context,
  });
  if ('error' in response) {
    return response;
  }
  const source = response.data;
  const context = {...source.context, bot_conversationId: source.id};
  const client = new ThreadsConversationClient(
    {conversationId: source.id},
    options,
  );
  try {
    const model = new Conversation(client, {
      ...source,
      user: params.participant,
      context,
      contextAfterRestore: withConversationOverview(
        source.id,
        {},
        getConversationOverview(context),
      ),
      cleanupOnFailedLeave: false,
      syncStorage: client.syncStorageMode,
      getSyncStorageMode: () => client.syncStorageMode,
    });
    if (model.status !== source.status) {
      await model.handleEventFromClient({
        id: uuid(),
        timestamp: Date.now(),
        name: 'conversation-updated',
        conversationId: source.id,
        status: source.status,
      });
    }
    client.attachModel(model);
    return {code: 200, data: {conversation: model}};
  } catch (error) {
    client.dispose();
    throw error;
  }
}
